import { useEffect, useState, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { ShoppingCart, Search, Plus, ChevronRight, X, LayoutGrid, List, Grid3X3, Instagram, Facebook, Globe, MessageCircle } from 'lucide-react'
import { toast } from 'sonner'
import { motion, AnimatePresence } from 'framer-motion'
import Logo from '@/components/Logo'
import { CartDrawer } from '@/components/cart/CartDrawer'
import { ProductDetailModal } from '@/components/ProductDetailModal'
import ProductCustomizationModal from '@/components/ProductCustomizationModal'
import { ActiveOrderCard } from '@/components/ActiveOrderCard'
import { useCartStore } from '@/stores/cartStore'
import LanguageSelector from '@/components/LanguageSelector'
import { LanguageProvider, useLanguage } from '@/contexts/LanguageContext'
import { MenuThemeProvider } from '@/contexts/MenuThemeContext'
import { getAssetUrl } from '@/config/api'
import { OrderItemCustomizations, MenuThemeId, MenuViewMode } from '@/types'
import { getTheme, getThemeCSSVariables } from '@/config/menuThemes'
import { HeroDecorations, BackgroundPattern } from '@/components/menu/ThemeDecorations'

// TikTok icon component
const TikTokIcon = ({ size = 20, className = '' }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
)

interface MenuItem {
  id: string
  name: string
  description?: string
  price: number
  imageUrl?: string
  categoryId: string
  available: boolean
  allergens?: string[]
  tags?: string[]
  isPopular?: boolean
  isNew?: boolean
  isChefRecommended?: boolean
  isVegetarian?: boolean
  isVegan?: boolean
  customizationOptions?: any
}

interface Category {
  id: string
  name: string
  displayOrder: number
}

interface MenuData {
  restaurantId: string
  restaurantName: string
  logoUrl?: string
  tableNumber: string
  sessionCode: string
  categories: Category[]
  products: MenuItem[]
  currentLanguage?: string
  supportedLanguages?: string[]
  // Menu customization settings
  menuTheme?: MenuThemeId
  defaultViewMode?: MenuViewMode
  showProductBadges?: boolean
  showPreparationTime?: boolean
  showAllergens?: boolean
  // Social links
  instagramUrl?: string
  facebookUrl?: string
  tiktokUrl?: string
  websiteUrl?: string
  whatsappNumber?: string
}

// Wrapper component for language provider
function CustomerMenuPageContent() {
  const { restaurantSlug, tableNumber } = useParams()
  const [searchParams] = useSearchParams()
  const [menuData, setMenuData] = useState<MenuData | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [showCart, setShowCart] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null)
  const [customizingProduct, setCustomizingProduct] = useState<MenuItem | null>(null)
  const [activeOrders, setActiveOrders] = useState<any[]>([])
  const [showSearch, setShowSearch] = useState(false)
  const [addedProductId, setAddedProductId] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<MenuViewMode>('grid3')
  const { t } = useLanguage()

  // Get the theme based on restaurant settings
  const theme = useMemo(() => getTheme(menuData?.menuTheme), [menuData?.menuTheme])
  const themeVars = useMemo(() => getThemeCSSVariables(theme), [theme])

  // Settings with defaults
  const showBadges = menuData?.showProductBadges ?? true
  const showAllergens = menuData?.showAllergens ?? true
  const hasSocialLinks = menuData?.instagramUrl || menuData?.facebookUrl || menuData?.tiktokUrl || menuData?.websiteUrl || menuData?.whatsappNumber

  const { addItem, itemCount, setSession, items, total } = useCartStore()

  const getCustomerId = () => {
    let customerId = localStorage.getItem('qorder_customer_id')
    if (!customerId) {
      customerId = crypto.randomUUID()
      localStorage.setItem('qorder_customer_id', customerId)
    }
    return customerId
  }

  const customerId = getCustomerId()

  useEffect(() => {
    loadMenu()
    loadActiveOrders()
    const interval = setInterval(loadActiveOrders, 10000)
    return () => clearInterval(interval)
  }, [restaurantSlug, tableNumber])

  const loadActiveOrders = async () => {
    try {
      const response = await fetch(`/api/public/orders/customer/${customerId}/active`)
      if (response.ok) {
        const orders = await response.json()
        setActiveOrders(orders)
      }
    } catch (error) {
      console.error('Error loading active orders:', error)
    }
  }

  const loadMenu = async () => {
    try {
      const lang = searchParams.get('lang') || 'it'
      const response = await fetch(
        `/api/public/menu/${restaurantSlug}/table/${tableNumber}?lang=${lang}`
      )
      const data = await response.json()
      setMenuData(data)

      if (data.sessionCode && data.restaurantId) {
        setSession(data.sessionCode, customerId, data.restaurantId, data.menuTheme)
        const tableLink = `/${restaurantSlug}/table/${tableNumber}`
        localStorage.setItem('qorder_table_link', tableLink)
      }

      if (data.categories.length > 0) {
        setSelectedCategory(data.categories[0].id)
      }

      // Set default view mode from restaurant settings
      if (data.defaultViewMode) {
        setViewMode(data.defaultViewMode)
      }
    } catch (error) {
      console.error('Error loading menu:', error)
      toast.error(t('menu.error') || 'Error loading menu')
    } finally {
      setLoading(false)
    }
  }

  const addToCart = (product: MenuItem, quantity: number = 1, customizations?: OrderItemCustomizations | null) => {
    if (!product.available) {
      toast.error('Questo prodotto non è al momento disponibile')
      return
    }

    addItem(
      { id: product.id, name: product.name, price: product.price, imageUrl: product.imageUrl },
      quantity,
      customizations
    )

    // Trigger add animation
    setAddedProductId(product.id)
    setTimeout(() => setAddedProductId(null), 600)

    toast.success(
      <div className="flex items-center gap-2">
        <span className="text-lg">✓</span>
        <span><strong>{product.name}</strong> aggiunto</span>
      </div>,
      { duration: 1500 }
    )
  }

  // Loading State - Themed
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.backgroundColor }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 mx-auto mb-6 border-4 rounded-full"
            style={{ borderColor: theme.primaryColor, borderTopColor: 'transparent' }}
          />
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-lg font-light tracking-wider"
            style={{ color: theme.mutedColor }}
          >
            Preparando il menu...
          </motion.p>
        </motion.div>
      </div>
    )
  }

  if (!menuData) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.backgroundColor }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center px-8"
        >
          <div className="text-8xl mb-6">🍽️</div>
          <h1 className="text-3xl font-bold mb-3" style={{ color: theme.textColor }}>Tavolo non trovato</h1>
          <p style={{ color: theme.mutedColor }}>Scansiona nuovamente il QR code</p>
        </motion.div>
      </div>
    )
  }

  const searchFilteredProducts = menuData.products.filter(p => {
    const matchesSearch = !searchQuery ||
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesSearch
  })

  const productsByCategory = menuData.categories.map(category => ({
    category,
    products: searchFilteredProducts.filter(p => p.categoryId === category.id)
  })).filter(group => group.products.length > 0)

  const scrollToCategory = (categoryId: string) => {
    setSelectedCategory(categoryId)
    const element = document.getElementById(`category-${categoryId}`)
    if (element) {
      const offset = 160
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - offset
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
    }
  }

  // Get card style based on theme
  const getCardClasses = () => {
    switch (theme.cardStyle) {
      case 'luxury':
        return 'border border-amber-900/30 shadow-lg shadow-amber-900/10'
      case 'neon':
        return 'border border-cyan-500/20 shadow-lg shadow-cyan-500/10'
      case 'rustic':
        return 'border border-amber-800/20'
      case 'glass':
        return 'backdrop-blur-md border border-white/10'
      case 'festive':
        return 'border border-red-900/30 shadow-lg shadow-red-900/10'
      default:
        return ''
    }
  }

  return (
    <MenuThemeProvider theme={theme}>
    <div className="min-h-screen" style={{ ...themeVars, backgroundColor: theme.backgroundColor } as React.CSSProperties}>

      {/* ============================================ */}
      {/* HERO SECTION - Theme-specific decorations */}
      {/* ============================================ */}
      <div className="relative overflow-hidden">
        {/* Theme-specific decorations */}
        <HeroDecorations theme={theme} />

        {/* Top Bar */}
        <div className="relative z-20">
          <div className="flex items-center justify-between p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-2"
            >
              <div
                className="w-10 h-10 rounded-full backdrop-blur-xl flex items-center justify-center border shadow-lg"
                style={{
                  backgroundColor: `${theme.primaryColor}20`,
                  borderColor: `${theme.primaryColor}40`
                }}
              >
                <span className="font-bold text-sm" style={{ color: theme.textColor }}>{menuData.tableNumber}</span>
              </div>
              <span className="text-sm" style={{ color: theme.mutedColor }}>Tavolo</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3"
            >
              <LanguageSelector variant="minimal" />
              <button
                onClick={() => setShowSearch(!showSearch)}
                className="w-10 h-10 rounded-full backdrop-blur-xl flex items-center justify-center border transition-all"
                style={{
                  backgroundColor: `${theme.primaryColor}20`,
                  borderColor: `${theme.primaryColor}40`
                }}
              >
                <Search size={18} style={{ color: theme.textColor }} />
              </button>
            </motion.div>
          </div>
        </div>

        {/* Hero Content - Centered */}
        <div className="relative z-10 flex flex-col items-center justify-center py-10 sm:py-14 px-6">
          {/* Logo with theme-specific effects */}
          {menuData.logoUrl ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.5, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 150, damping: 20 }}
              className="mb-5 relative"
            >
              {/* Outer glow ring - themed */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="absolute -inset-3 rounded-[32px]"
                style={{
                  background: `conic-gradient(from 0deg, transparent, ${theme.primaryColor}50, transparent, ${theme.secondaryColor}50, transparent)`
                }}
              />

              {/* Soft glow backdrop - themed */}
              <div
                className="absolute -inset-4 rounded-[36px] blur-2xl opacity-60"
                style={{
                  background: `linear-gradient(135deg, ${theme.primaryColor}60, ${theme.secondaryColor}40, ${theme.accentColor}30)`
                }}
              />

              {/* Logo container */}
              <div
                className="relative p-1 rounded-[28px]"
                style={{
                  background: `linear-gradient(135deg, ${theme.textColor}30, ${theme.textColor}10, ${theme.textColor}05)`
                }}
              >
                <div
                  className="relative rounded-[24px] p-1 overflow-hidden"
                  style={{ backgroundColor: theme.cardBackground }}
                >
                  {/* Shine effect */}
                  <motion.div
                    animate={{ x: ['-100%', '200%'] }}
                    transition={{ duration: 3, repeat: Infinity, repeatDelay: 2, ease: "easeInOut" }}
                    className="absolute inset-0 w-1/2 skew-x-12 z-10"
                    style={{
                      background: `linear-gradient(90deg, transparent, ${theme.textColor}15, transparent)`
                    }}
                  />
                  <img
                    src={getAssetUrl(menuData.logoUrl)}
                    alt={menuData.restaurantName}
                    className="relative w-28 h-28 sm:w-36 sm:h-36 object-contain rounded-[20px]"
                  />
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
              className="relative mb-5"
            >
              <div
                className="absolute -inset-4 rounded-3xl blur-2xl"
                style={{
                  background: `linear-gradient(135deg, ${theme.primaryColor}50, ${theme.secondaryColor}30)`
                }}
              />
              <div
                className="relative w-24 h-24 sm:w-32 sm:h-32 rounded-2xl flex items-center justify-center shadow-2xl border"
                style={{
                  background: theme.buttonGradient,
                  boxShadow: `0 25px 50px -12px ${theme.shadowColor}`,
                  borderColor: `${theme.textColor}10`
                }}
              >
                <span className="text-5xl sm:text-6xl">{theme.emoji}</span>
              </div>
            </motion.div>
          )}

          {/* Restaurant Name with gradient */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-black text-center mb-2 tracking-tight"
            style={{
              color: theme.textColor,
              textShadow: theme.cardStyle === 'neon' ? `0 0 30px ${theme.primaryColor}` : 'none'
            }}
          >
            {menuData.restaurantName}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="text-sm sm:text-base text-center mb-5"
            style={{ color: theme.mutedColor }}
          >
            Scopri il nostro menu
          </motion.p>

          {/* Powered by badge - themed */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="flex items-center gap-1.5 text-[11px] backdrop-blur-sm px-3 py-1.5 rounded-full border"
            style={{
              backgroundColor: `${theme.cardBackground}80`,
              color: theme.mutedColor,
              borderColor: theme.borderColor
            }}
          >
            <span>Powered by</span>
            <Logo size={12} variant="icon" />
            <span className="font-medium" style={{ color: theme.primaryColor }}>Qorder</span>
          </motion.div>
        </div>

        {/* Bottom fade */}
        <div
          className="absolute bottom-0 left-0 right-0 h-24"
          style={{
            background: `linear-gradient(to top, ${theme.backgroundColor}, ${theme.backgroundColor}80, transparent)`
          }}
        />
      </div>

      {/* ============================================ */}
      {/* SEARCH OVERLAY */}
      {/* ============================================ */}
      <AnimatePresence>
        {showSearch && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 backdrop-blur-xl z-50 flex flex-col"
            style={{ backgroundColor: `${theme.backgroundColor}f5` }}
          >
            <div className="p-4 flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2" size={20} style={{ color: theme.mutedColor }} />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cerca nel menu..."
                  autoFocus
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  name="menu-search"
                  className="w-full pl-12 pr-4 py-4 border rounded-2xl text-lg"
                  style={{
                    backgroundColor: `${theme.cardBackground}`,
                    borderColor: theme.borderColor,
                    color: theme.textColor
                  }}
                />
              </div>
              <button
                onClick={() => { setShowSearch(false); setSearchQuery(''); }}
                className="w-12 h-12 rounded-full flex items-center justify-center transition-all"
                style={{ backgroundColor: theme.cardBackground }}
              >
                <X size={24} style={{ color: theme.textColor }} />
              </button>
            </div>

            {searchQuery && (
              <div className="flex-1 overflow-y-auto p-4">
                <p className="text-sm mb-4" style={{ color: theme.mutedColor }}>{searchFilteredProducts.length} risultati</p>
                <div className="space-y-3">
                  {searchFilteredProducts.map(product => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      onClick={() => { setSelectedProduct(product); setShowSearch(false); }}
                      className="flex items-center gap-4 p-4 rounded-2xl border cursor-pointer transition-all"
                      style={{
                        backgroundColor: theme.cardBackground,
                        borderColor: theme.borderColor
                      }}
                    >
                      <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0" style={{ backgroundColor: theme.cardBackgroundHover }}>
                        {product.imageUrl ? (
                          <img src={product.imageUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-2xl">🍽️</div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold truncate" style={{ color: theme.textColor }}>{product.name}</h3>
                        <p className="text-sm truncate" style={{ color: theme.mutedColor }}>{product.description}</p>
                      </div>
                      <span className="font-bold" style={{ color: theme.primaryColor }}>€{product.price.toFixed(2)}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ============================================ */}
      {/* CATEGORIES - Sticky with theme styling */}
      {/* ============================================ */}
      <div
        className="sticky top-0 z-30 backdrop-blur-xl border-b"
        style={{
          backgroundColor: `${theme.backgroundColor}f2`,
          borderColor: theme.borderColor
        }}
      >
        <div className="px-3 sm:px-6 py-3">
          {/* Categories Grid */}
          <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 mb-2">
            {menuData.categories.map((category, index) => {
              const isActive = selectedCategory === category.id
              const categoryProducts = menuData.products.filter(p => p.categoryId === category.id)

              return (
                <motion.button
                  key={category.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.03, type: "spring", stiffness: 400 }}
                  onClick={() => scrollToCategory(category.id)}
                  className="relative group px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-300"
                  style={{
                    background: isActive ? theme.buttonGradient : `${theme.cardBackground}`,
                    color: isActive ? '#fff' : theme.mutedColor,
                    boxShadow: isActive ? `0 4px 20px ${theme.shadowColor}` : 'none',
                    border: `1px solid ${isActive ? 'transparent' : theme.borderColor}`,
                    transform: isActive ? 'scale(1.05)' : 'scale(1)'
                  }}
                >
                  <span className="relative z-10">{category.name}</span>
                  <span
                    className="ml-1.5 text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full transition-all"
                    style={{
                      backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : `${theme.borderColor}`,
                      color: isActive ? '#fff' : theme.mutedColor
                    }}
                  >
                    {categoryProducts.length}
                  </span>
                </motion.button>
              )
            })}
          </div>

          {/* View Toggle */}
          <div className="flex items-center justify-center gap-1">
            <div className="flex items-center gap-0.5 rounded-lg p-0.5" style={{ backgroundColor: theme.cardBackground }}>
              {[
                { mode: 'grid3' as MenuViewMode, icon: LayoutGrid, title: 'Griglia 3' },
                { mode: 'grid4' as MenuViewMode, icon: Grid3X3, title: 'Griglia 4' },
                { mode: 'list' as MenuViewMode, icon: List, title: 'Lista' },
              ].map(({ mode, icon: Icon, title }) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className="p-1.5 rounded-md transition-all"
                  style={{
                    backgroundColor: viewMode === mode ? `${theme.primaryColor}30` : 'transparent',
                    color: viewMode === mode ? theme.primaryColor : theme.mutedColor
                  }}
                  title={title}
                >
                  <Icon size={14} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* ACTIVE ORDERS */}
      {/* ============================================ */}
      {activeOrders.length > 0 && (
        <div className="px-4 sm:px-6 py-6">
          <h2 className="font-bold text-lg mb-4 flex items-center gap-2" style={{ color: theme.textColor }}>
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: theme.accentColor }} />
            Ordini in corso
          </h2>
          <div className="space-y-3">
            {activeOrders.map(order => (
              <ActiveOrderCard key={order.id} order={order} />
            ))}
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* PRODUCTS BY CATEGORY */}
      {/* ============================================ */}
      <div className="px-4 sm:px-6 py-6 pb-32">
        {productsByCategory.map(({ category, products }) => (
          <div key={category.id} id={`category-${category.id}`} className="mb-12">
            {/* Category Header - themed */}
            <div className="flex items-center gap-4 mb-6">
              <h2
                className="text-2xl sm:text-3xl font-black"
                style={{
                  color: theme.textColor,
                  textShadow: theme.cardStyle === 'neon' ? `0 0 20px ${theme.primaryColor}50` : 'none'
                }}
              >
                {category.name}
              </h2>
              <div
                className="h-[1px] flex-1"
                style={{
                  background: `linear-gradient(to right, ${theme.primaryColor}40, transparent)`
                }}
              />
              <span className="text-sm" style={{ color: theme.mutedColor }}>{products.length} piatti</span>
            </div>

            {/* Products Grid */}
            <div className={`grid gap-4 sm:gap-5 ${
              viewMode === 'list'
                ? 'grid-cols-1'
                : viewMode === 'grid4'
                  ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'
                  : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
            }`}>
              {products.map((product, index) => {
                const isUnavailable = !product.available
                const isAdded = addedProductId === product.id

                // LIST VIEW
                if (viewMode === 'list') {
                  return (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.03 }}
                      onClick={() => !isUnavailable && setSelectedProduct(product)}
                      className={`group relative flex items-center gap-4 p-3 rounded-2xl cursor-pointer transition-all duration-300 ${getCardClasses()} ${
                        isUnavailable ? 'opacity-40 grayscale' : ''
                      } ${isAdded ? 'ring-2 ring-green-400' : ''}`}
                      style={{
                        backgroundColor: theme.cardBackground,
                        borderRadius: theme.cardBorderRadius
                      }}
                    >
                      <div
                        className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden shrink-0"
                        style={{ backgroundColor: theme.cardBackgroundHover }}
                      >
                        {product.imageUrl ? (
                          <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-3xl">🍽️</div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold text-sm sm:text-base line-clamp-1" style={{ color: theme.textColor }}>{product.name}</h3>
                          <span className="font-bold text-base sm:text-lg shrink-0" style={{ color: theme.primaryColor }}>€{product.price.toFixed(2)}</span>
                        </div>
                        {product.description && (
                          <p className="text-xs sm:text-sm line-clamp-1 mt-1" style={{ color: theme.mutedColor }}>{product.description}</p>
                        )}
                        {showBadges && (
                          <div className="flex gap-1.5 mt-2">
                            {product.isPopular && <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: `${theme.primaryColor}20`, color: theme.primaryColor }}>🔥 Popolare</span>}
                            {product.isVegan && <span className="text-[10px] bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">🌱 Vegano</span>}
                            {product.isVegetarian && !product.isVegan && <span className="text-[10px] bg-lime-500/20 text-lime-400 px-2 py-0.5 rounded-full">🥬 Veg</span>}
                          </div>
                        )}
                      </div>

                      <ChevronRight size={20} className="shrink-0 transition-colors" style={{ color: theme.mutedColor }} />
                    </motion.div>
                  )
                }

                // GRID VIEW
                return (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ delay: index * 0.08, duration: 0.5 }}
                    className="group relative h-full"
                  >
                    <div
                      className={`relative rounded-2xl overflow-hidden transition-all duration-700 h-full flex flex-col ${getCardClasses()} ${
                        isUnavailable ? 'opacity-40 grayscale' : ''
                      } ${isAdded ? 'ring-2 ring-green-400 ring-offset-2' : ''}`}
                      style={{
                        backgroundColor: theme.cardBackground,
                        borderRadius: theme.cardBorderRadius,
                        '--tw-ring-offset-color': theme.backgroundColor
                      } as React.CSSProperties}
                    >
                      {/* Animated border gradient for luxury/neon */}
                      {(theme.cardStyle === 'luxury' || theme.cardStyle === 'neon') && (
                        <div
                          className="absolute -inset-[1px] rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                          style={{
                            background: `linear-gradient(135deg, ${theme.primaryColor}40, ${theme.secondaryColor}20, ${theme.primaryColor}40)`,
                            borderRadius: theme.cardBorderRadius
                          }}
                        />
                      )}

                      <div className="relative h-full flex flex-col" style={{ backgroundColor: theme.cardBackground, borderRadius: theme.cardBorderRadius }}>
                        {/* Image Container */}
                        <div
                          onClick={() => !isUnavailable && setSelectedProduct(product)}
                          className="relative overflow-hidden cursor-pointer aspect-[4/3] shrink-0"
                        >
                          {product.imageUrl ? (
                            <>
                              <img
                                src={product.imageUrl}
                                alt={product.name}
                                className="w-full h-full object-cover transition-all duration-1000 ease-out group-hover:scale-110"
                              />
                              <div
                                className="absolute inset-0"
                                style={{
                                  background: `linear-gradient(to top, ${theme.cardBackground}, transparent 50%)`
                                }}
                              />
                              <div
                                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                                style={{
                                  background: `linear-gradient(135deg, ${theme.primaryColor}10, transparent)`
                                }}
                              />
                            </>
                          ) : (
                            <div
                              className="w-full h-full flex items-center justify-center"
                              style={{
                                background: `linear-gradient(135deg, ${theme.cardBackgroundHover}, ${theme.cardBackground})`
                              }}
                            >
                              <div className="relative">
                                <div
                                  className="absolute inset-0 blur-3xl rounded-full scale-150 opacity-50"
                                  style={{ backgroundColor: `${theme.primaryColor}30` }}
                                />
                                <span className="relative text-5xl">{theme.emoji}</span>
                              </div>
                            </div>
                          )}

                          {/* Top Badges */}
                          {showBadges && (
                            <div className="absolute top-2 left-2 right-2 flex justify-between items-start">
                              <div className="flex flex-wrap gap-1">
                                {isUnavailable && (
                                  <span className="backdrop-blur-md text-[10px] font-medium px-2 py-1 rounded-full border" style={{ backgroundColor: `${theme.backgroundColor}b0`, color: theme.textSecondary, borderColor: theme.borderColor }}>
                                    Non disponibile
                                  </span>
                                )}
                                {!isUnavailable && product.isPopular && (
                                  <span
                                    className="text-white text-[10px] font-semibold px-2 py-1 rounded-full shadow-lg"
                                    style={{
                                      background: theme.buttonGradient,
                                      boxShadow: `0 4px 15px ${theme.shadowColor}`
                                    }}
                                  >
                                    🔥 Popolare
                                  </span>
                                )}
                                {!isUnavailable && product.isChefRecommended && !product.isPopular && (
                                  <span className="bg-gradient-to-r from-violet-500 to-purple-500 text-white text-[10px] font-semibold px-2 py-1 rounded-full shadow-lg shadow-purple-500/30">
                                    ⭐ Chef
                                  </span>
                                )}
                                {!isUnavailable && product.isNew && (
                                  <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 text-white text-[10px] font-semibold px-2 py-1 rounded-full shadow-lg shadow-emerald-500/30">
                                    Nuovo
                                  </span>
                                )}
                              </div>

                              {!isUnavailable && (product.isVegan || product.isVegetarian) && (
                                <div
                                  className="backdrop-blur-md px-2 py-1 rounded-full border"
                                  style={{ backgroundColor: `${theme.backgroundColor}80`, borderColor: theme.borderColor }}
                                >
                                  {product.isVegan ? (
                                    <span className="text-green-400 text-[10px] font-medium">🌱</span>
                                  ) : (
                                    <span className="text-lime-400 text-[10px] font-medium">🥬</span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="p-3 flex flex-col flex-1">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h3
                              className="text-sm font-semibold leading-tight line-clamp-2 flex-1"
                              style={{ color: theme.textColor }}
                            >
                              {product.name}
                            </h3>
                            <span
                              className="text-base font-bold shrink-0"
                              style={{ color: theme.primaryColor }}
                            >
                              €{product.price.toFixed(2)}
                            </span>
                          </div>

                          {product.description && (
                            <p
                              className="text-[11px] leading-relaxed line-clamp-2 mb-2"
                              style={{ color: theme.mutedColor }}
                            >
                              {product.description}
                            </p>
                          )}

                          {/* Allergens */}
                          {showAllergens && product.allergens && product.allergens.length > 0 && (
                            <div className="flex items-center gap-1 mb-2 flex-wrap">
                              <span className="text-[9px] uppercase" style={{ color: theme.mutedColor }}>Allergeni:</span>
                              {product.allergens.slice(0, 2).map((allergen, i) => (
                                <span
                                  key={i}
                                  className="text-[9px] px-1.5 py-0.5 rounded"
                                  style={{ color: theme.textSecondary, backgroundColor: `${theme.borderColor}` }}
                                >
                                  {allergen}
                                </span>
                              ))}
                              {product.allergens.length > 2 && (
                                <span className="text-[9px]" style={{ color: theme.mutedColor }}>
                                  +{product.allergens.length - 2}
                                </span>
                              )}
                            </div>
                          )}

                          <div className="flex-1" />

                          {/* Bottom action bar */}
                          <div
                            className="flex items-center justify-between pt-2 border-t mt-auto"
                            style={{ borderColor: theme.borderColor }}
                          >
                            <button
                              onClick={() => setSelectedProduct(product)}
                              className="text-[11px] transition-colors flex items-center gap-0.5"
                              style={{ color: theme.mutedColor }}
                            >
                              Dettagli
                              <ChevronRight size={12} />
                            </button>

                            <motion.button
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                if (product.customizationOptions) {
                                  setCustomizingProduct(product)
                                } else {
                                  addToCart(product)
                                }
                              }}
                              disabled={isUnavailable}
                              className={`h-7 rounded-lg text-[11px] font-medium transition-all flex items-center gap-1 ${
                                isUnavailable ? 'cursor-not-allowed px-2' : 'text-white px-2.5'
                              }`}
                              style={{
                                background: isUnavailable ? theme.borderColor : theme.buttonGradient,
                                color: isUnavailable ? theme.mutedColor : '#fff',
                                boxShadow: isUnavailable ? 'none' : `0 4px 15px ${theme.shadowColor}`
                              }}
                            >
                              <Plus size={12} strokeWidth={2.5} />
                              Aggiungi
                            </motion.button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Added animation overlay */}
                    <AnimatePresence>
                      {isAdded && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="absolute inset-0 rounded-2xl bg-green-500/10 backdrop-blur-sm flex items-center justify-center pointer-events-none z-10"
                        >
                          <motion.div
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            exit={{ scale: 0, rotate: 180 }}
                            transition={{ type: "spring", stiffness: 300, damping: 20 }}
                            className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-2xl shadow-green-500/50"
                          >
                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      {/* ============================================ */}
      {/* FOOTER */}
      {/* ============================================ */}
      <footer
        className="mt-8 pb-32 border-t"
        style={{
          borderColor: theme.borderColor,
          background: `linear-gradient(180deg, transparent 0%, ${theme.backgroundColor}99 100%)`
        }}
      >
        {/* Social Links */}
        {hasSocialLinks && (
          <div className="px-4 sm:px-6 py-8">
            <p
              className="text-center text-sm font-medium mb-4 uppercase tracking-wider"
              style={{ color: theme.mutedColor }}
            >
              Seguici sui social
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {menuData.instagramUrl && (
                <a
                  href={menuData.instagramUrl.startsWith('http') ? menuData.instagramUrl : `https://instagram.com/${menuData.instagramUrl.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 px-5 py-2.5 rounded-full transition-all duration-300 hover:scale-105"
                  style={{
                    backgroundColor: theme.cardBackground,
                    color: theme.textColor,
                    border: `1px solid ${theme.borderColor}`,
                    boxShadow: `0 4px 15px ${theme.shadowColor}`
                  }}
                >
                  <Instagram size={20} className="transition-transform group-hover:scale-110" style={{ color: '#E4405F' }} />
                  <span className="text-sm font-medium">Instagram</span>
                </a>
              )}
              {menuData.facebookUrl && (
                <a
                  href={menuData.facebookUrl.startsWith('http') ? menuData.facebookUrl : `https://facebook.com/${menuData.facebookUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 px-5 py-2.5 rounded-full transition-all duration-300 hover:scale-105"
                  style={{
                    backgroundColor: theme.cardBackground,
                    color: theme.textColor,
                    border: `1px solid ${theme.borderColor}`,
                    boxShadow: `0 4px 15px ${theme.shadowColor}`
                  }}
                >
                  <Facebook size={20} className="transition-transform group-hover:scale-110" style={{ color: '#1877F2' }} />
                  <span className="text-sm font-medium">Facebook</span>
                </a>
              )}
              {menuData.tiktokUrl && (
                <a
                  href={menuData.tiktokUrl.startsWith('http') ? menuData.tiktokUrl : `https://tiktok.com/${menuData.tiktokUrl.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 px-5 py-2.5 rounded-full transition-all duration-300 hover:scale-105"
                  style={{
                    backgroundColor: theme.cardBackground,
                    color: theme.textColor,
                    border: `1px solid ${theme.borderColor}`,
                    boxShadow: `0 4px 15px ${theme.shadowColor}`
                  }}
                >
                  <TikTokIcon size={20} className="transition-transform group-hover:scale-110" />
                  <span className="text-sm font-medium">TikTok</span>
                </a>
              )}
              {menuData.websiteUrl && (
                <a
                  href={menuData.websiteUrl.startsWith('http') ? menuData.websiteUrl : `https://${menuData.websiteUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 px-5 py-2.5 rounded-full transition-all duration-300 hover:scale-105"
                  style={{
                    backgroundColor: theme.cardBackground,
                    color: theme.textColor,
                    border: `1px solid ${theme.borderColor}`,
                    boxShadow: `0 4px 15px ${theme.shadowColor}`
                  }}
                >
                  <Globe size={20} className="transition-transform group-hover:scale-110" style={{ color: theme.primaryColor }} />
                  <span className="text-sm font-medium">Sito Web</span>
                </a>
              )}
              {menuData.whatsappNumber && (
                <a
                  href={`https://wa.me/${menuData.whatsappNumber.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 px-5 py-2.5 rounded-full transition-all duration-300 hover:scale-105"
                  style={{
                    backgroundColor: theme.cardBackground,
                    color: theme.textColor,
                    border: `1px solid ${theme.borderColor}`,
                    boxShadow: `0 4px 15px ${theme.shadowColor}`
                  }}
                >
                  <MessageCircle size={20} className="transition-transform group-hover:scale-110" style={{ color: '#25D366' }} />
                  <span className="text-sm font-medium">WhatsApp</span>
                </a>
              )}
            </div>
          </div>
        )}

        {/* Restaurant Info & Powered by */}
        <div className="px-4 sm:px-6 py-6 text-center">
          {/* Restaurant Name */}
          <div className="flex items-center justify-center gap-3 mb-4">
            {menuData?.logoUrl ? (
              <img
                src={getAssetUrl(menuData.logoUrl)}
                alt={menuData.restaurantName}
                className="w-10 h-10 rounded-xl object-cover"
                style={{ border: `1px solid ${theme.borderColor}` }}
              />
            ) : (
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
                style={{ background: theme.buttonGradient }}
              >
                {theme.emoji}
              </div>
            )}
            <span
              className="text-lg font-bold"
              style={{ color: theme.textColor }}
            >
              {menuData?.restaurantName}
            </span>
          </div>

          {/* Divider */}
          <div
            className="w-16 h-0.5 mx-auto mb-4 rounded-full"
            style={{ background: theme.buttonGradient, opacity: 0.5 }}
          />

          {/* Powered by Qorder */}
          <p
            className="text-xs flex items-center justify-center gap-1.5"
            style={{ color: theme.mutedColor }}
          >
            Powered by
            <span
              className="font-bold"
              style={{ color: theme.primaryColor }}
            >
              Qorder
            </span>
          </p>
        </div>
      </footer>

      {/* ============================================ */}
      {/* FLOATING CART BUTTON */}
      {/* ============================================ */}
      <AnimatePresence>
        {itemCount() > 0 && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-6 left-4 right-4 z-40"
          >
            <button
              onClick={() => setShowCart(true)}
              className="w-full text-white rounded-2xl p-4 flex items-center justify-between shadow-2xl transition-all"
              style={{
                background: theme.buttonGradient,
                boxShadow: `0 25px 50px -12px ${theme.shadowColor}`
              }}
            >
              <div className="flex items-center gap-3">
                <div className="relative">
                  <ShoppingCart size={24} />
                  <span
                    className="absolute -top-2 -right-2 w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center"
                    style={{ backgroundColor: '#fff', color: theme.primaryColor }}
                  >
                    {itemCount()}
                  </span>
                </div>
                <span className="font-bold">Vedi carrello</span>
              </div>
              <span className="text-xl font-black">€{total().toFixed(2)}</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ============================================ */}
      {/* MODALS */}
      {/* ============================================ */}
      <ProductDetailModal
        product={selectedProduct}
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        onAddToCart={addToCart}
        onCustomize={(product) => {
          setSelectedProduct(null)
          setCustomizingProduct(product)
        }}
      />

      {customizingProduct && (
        <ProductCustomizationModal
          product={customizingProduct as any}
          isOpen={!!customizingProduct}
          onClose={() => setCustomizingProduct(null)}
          onAddToCart={(_product, customizations, quantity) => {
            addToCart(customizingProduct, quantity, customizations)
            setCustomizingProduct(null)
          }}
        />
      )}

      <CartDrawer isOpen={showCart} onClose={() => setShowCart(false)} />

      {/* Global Styles */}
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
    </MenuThemeProvider>
  )
}

export default function CustomerMenuPage() {
  const [searchParams] = useSearchParams()
  const lang = searchParams.get('lang') || 'it'

  return (
    <LanguageProvider initialLanguage={lang} supportedLanguages={['it', 'en', 'fr', 'de', 'es']}>
      <CustomerMenuPageContent />
    </LanguageProvider>
  )
}
